/**
 * ReportIncidentModal — entry form for an HSE Incident (DEV-822 / DEV-831).
 *
 * Opens from the "+ Report" button on each HSE card with the category
 * pre-filled and locked. The same form is reachable from the standard
 * Issues entry path with the category editable (handled by DEV-832).
 *
 * Saves a ProjectIssue with subtype='hse_incident'. The backend enforces:
 *   - incident_date, hse_category, severity_hse all required
 *   - incident_date immutable after first save (so this form is creation-only)
 *   - confidential cascades to title/description visibility for non-authorized viewers
 *
 * Spec: docs/features/Life4Earth_HSE_Feature_Spec.md § 8
 * Mockup: mockups/hse_incident_entry.html
 */

import React, { useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import {
  createProjectIssue,
  createMiniProjectHseIncident,
  type CreateProjectIssueRequest,
} from '../../api/projectIssues';
import { invalidateByPrefix, invalidateAndRefetch } from '../../api/queryCache';
import type {
  HseCategory,
  HseSeverity,
} from '../../kpi-engine/missionTypes/types';
import {
  severitiesForCategory,
  severityIsRed,
  severityIsYellow,
} from '../../kpi-engine/hse';
import { AttachmentPicker } from '../AttachmentPicker/AttachmentPicker';
import styles from './ReportIncidentModal.module.css';

// ── Props ────────────────────────────────────────────────────────

export interface ReportIncidentModalProps {
  open: boolean;
  projectGuid: string;
  /**
   * DEV-1141. When set, the incident is filed against a mini-project via
   * /api/mini-projects/{guid}/hse-incidents instead of the full-project
   * issues route. The full-project behavior is unchanged when omitted.
   */
  miniProjectGuid?: string;
  /**
   * Project name shown in a small breadcrumb above the modal title.
   * Stress-context confirmation: lets the user verify they're filing
   * against the right project before committing the immutable
   * incident_date. Optional — modal degrades gracefully without it.
   */
  projectName?: string;
  /**
   * When provided, locks the category and pre-selects it. Set by the
   * "+ Report" button on each HSE card. When omitted, the category is
   * a user-selectable picker (entry from the standard Issues form).
   */
  initialCategory?: HseCategory;
  onClose: () => void;
  /** Called with the new issue id after a successful save. */
  onSaved?: (issueId: string) => void;
}

// ── Severity model (per-category — DEV-822 Option B) ──────────────
// Severities and their color band depend on the HSE category. The first
// severity in each scale (near-miss) is non-confidential by default;
// everything else pre-checks Confidential because it usually involves
// named individuals or sensitive details.

function severityColor(severity: HseSeverity, category: HseCategory): 'yellow' | 'red' {
  return severityIsRed(severity, category)
    ? 'red'
    : severityIsYellow(severity, category)
    ? 'yellow'
    : 'yellow'; // shouldn't happen for valid pairings
}

function defaultsToConfidential(severity: HseSeverity): boolean {
  // Near-miss is the only severity that doesn't default-confidential —
  // it's a precursor event, usually no named individuals involved.
  return severity !== 'near-miss';
}

// ── Helpers ──────────────────────────────────────────────────────

/** Returns YYYY-MM-DDTHH:mm in local time (input[type=datetime-local] format). */
function nowLocalIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    d.getFullYear() +
    '-' +
    pad(d.getMonth() + 1) +
    '-' +
    pad(d.getDate()) +
    'T' +
    pad(d.getHours()) +
    ':' +
    pad(d.getMinutes())
  );
}

function localIsoToUtc(localIso: string): string {
  // Treat the local input as wall-clock; convert to ISO UTC for the API.
  const d = new Date(localIso);
  return d.toISOString();
}

// ── Component ────────────────────────────────────────────────────

export function ReportIncidentModal({
  open,
  projectGuid,
  miniProjectGuid,
  projectName,
  initialCategory,
  onClose,
  onSaved,
}: ReportIncidentModalProps) {
  const isMini = !!miniProjectGuid;
  // Owner used for file-attachment namespacing (FileUpload entityId).
  const ownerGuid = miniProjectGuid || projectGuid;
  const { t } = useAppTranslation('kpi');
  const { t: tCommon } = useAppTranslation('common');

  const categoryLocked = !!initialCategory;

  // Form state
  const [category, setCategory] = useState<HseCategory>(
    initialCategory ?? 'safety',
  );
  // Severity defaults to the first non-near-miss option for the current
  // category (typically the second item in the scale). Re-derived when
  // category changes since the valid severity set changes too.
  const [severity, setSeverity] = useState<HseSeverity>(() => {
    const opts = severitiesForCategory(initialCategory ?? 'safety');
    return opts[1] ?? opts[0];
  });
  const [incidentDate, setIncidentDate] = useState<string>(nowLocalIso());
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [confidential, setConfidential] = useState<boolean>(true);
  // DEV-822 #126: file attachments. AttachmentPicker uploads files
  // directly to MinIO; we collect the resulting keys and send them with
  // the create request body.
  const [attachmentKeys, setAttachmentKeys] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // The valid severity options swap when category changes (Option B).
  const severityOptions = useMemo(() => severitiesForCategory(category), [category]);

  // If category changes (e.g., from the standard Issues entry path) and
  // the current severity isn't valid for the new category, reset to the
  // category's default (second item in the scale).
  React.useEffect(() => {
    if (!severityOptions.includes(severity)) {
      setSeverity(severityOptions[1] ?? severityOptions[0]);
    }
  }, [severityOptions, severity]);

  // When severity changes, update the confidential default — but only if
  // the user hasn't already toggled it themselves.
  const [userTouchedConfidential, setUserTouchedConfidential] = useState(false);
  const effectiveConfidential = useMemo(() => {
    if (userTouchedConfidential) return confidential;
    return defaultsToConfidential(severity);
  }, [severity, confidential, userTouchedConfidential]);

  // Reset form state when the modal closes (so reopening gets a clean slate).
  const reset = useCallback(() => {
    setCategory(initialCategory ?? 'safety');
    setSeverity('first-aid');
    setIncidentDate(nowLocalIso());
    setTitle('');
    setDescription('');
    setConfidential(true);
    setUserTouchedConfidential(false);
    setAttachmentKeys([]);
    setError(null);
    setSubmitting(false);
  }, [initialCategory]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    reset();
    onClose();
  }, [submitting, reset, onClose]);

  const handleBackdropClick: React.MouseEventHandler = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const handleSave = useCallback(async () => {
    setError(null);

    // Front-end validation. Backend enforces these too.
    const trimmedTitle = title.trim();
    const trimmedDesc = description.trim();
    if (!trimmedTitle) {
      setError(t('hse.modal.error.titleRequired'));
      return;
    }
    if (!trimmedDesc) {
      setError(t('hse.modal.error.descriptionRequired'));
      return;
    }
    if (!incidentDate) {
      setError(t('hse.modal.error.incidentDateRequired'));
      return;
    }

    setSubmitting(true);
    try {
      const req: CreateProjectIssueRequest = {
        title: trimmedTitle,
        description: trimmedDesc,
        // Standard severity mirrors the HSE severity bucket so non-HSE
        // queries see something coherent.
        severity:
          severity === 'fatality' || severity === 'lost-time' || severity === 'recordable'
            ? 'blocking'
            : 'warning',
        // HSE-specific fields. The standard `category` field is auto-derived
        // by the controller from `hseCategory` — single source of truth lives
        // server-side so we don't have to maintain a mapping in the frontend.
        subtype: 'hse_incident',
        incidentDate: localIsoToUtc(incidentDate),
        hseCategory: category,
        severityHse: severity,
        confidential: effectiveConfidential,
        // File attachments (DEV-822 #126) — already uploaded to MinIO,
        // we're just persisting the keys with the new issue.
        attachmentFileKeys: attachmentKeys,
      };

      const created = isMini
        ? await createMiniProjectHseIncident(miniProjectGuid!, req)
        : await createProjectIssue(projectGuid, req);

      /*
       * DEV-852: Same fix as DEV-849 for the edit flow. Previously this
       * dispatched CACHE_UPDATE_EVENT raw without first marking cache
       * entries stale, so subscribers refetched but `fetchForKey` returned
       * cached data — the HSE card lagged the new incident by ~stale-time
       * (Skip saw a ~1min delay before both incidents appeared).
       *
       * `invalidateByPrefix('hse-incidents')` covers BOTH the per-project
       * key (`hse-incidents:<guid>` used by useHseIncidents — the drill-down
       * source) AND the bulk key (`hse-incidents-bulk:...` used by
       * useHseIncidentsBulk in App.tsx — the HSE card source). Each entry
       * is marked stale; notifySubscribers fires per key; refetches actually
       * hit the network and return the new incident immediately.
       */
      // MP incidents live under a separate cache prefix; FP under
      // 'hse-incidents' (covers per-project + bulk keys).
      invalidateByPrefix(isMini ? 'hse-mp-incidents' : 'hse-incidents');
      invalidateAndRefetch('project_issues');

      onSaved?.(created.id);
      reset();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      setSubmitting(false);
    }
  }, [
    title,
    description,
    incidentDate,
    category,
    severity,
    effectiveConfidential,
    attachmentKeys,
    projectGuid,
    miniProjectGuid,
    isMini,
    onSaved,
    reset,
    onClose,
    t,
  ]);

  if (!open) return null;

  // DEV-822: Render via portal to document.body so the modal escapes the
  // nested KpiNavigation stacking context (the persistent project sub-tabs
  // were winning the z-order otherwise).
  return createPortal(
    <div
      className={styles.backdrop}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="hse-modal-title"
    >
      <div className={styles.modal}>
        <div className={styles.head}>
          <div className={styles.headTitleStack}>
            {/* DEV-822: stress-context project breadcrumb. People logging
                incidents may have multiple tabs open across projects.
                Confirming "you're about to file against THIS project" before
                they hit Save catches the wrong-project mistake. */}
            {projectName && (
              <div
                className={styles.projectBreadcrumb}
                aria-label={t('hse.modal.projectContext')}
              >
                <span className={styles.projectBreadcrumbLabel}>
                  {t('hse.modal.projectLabel')}
                </span>
                <span className={styles.projectBreadcrumbSep}>·</span>
                <span className={styles.projectBreadcrumbName}>{projectName}</span>
              </div>
            )}
            <h3 id="hse-modal-title" className={styles.title}>
              {t('hse.modal.title')} —{' '}
              <span className={styles.titleCategory}>
                {t(`hse.category.${category}`)}
              </span>
            </h3>
          </div>
          {/* Subtitle clarifies that near-miss reporting is welcome here too,
              addressing the "Report incident" framing question (DEV-822). */}
          <button
            className={styles.closeBtn}
            onClick={handleClose}
            type="button"
            aria-label={tCommon('close')}
          >
            ×
          </button>
        </div>

        <div className={styles.body}>
          {/* Category — locked or editable */}
          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              {t('hse.modal.field.category')}
              <span className={styles.req}>*</span>
            </label>
            {categoryLocked ? (
              <div className={styles.fieldLocked}>
                <span>{t(`hse.category.${category}`)}</span>
                <span className={styles.lockIcon}>
                  🔒 {t('hse.modal.help.preFilledFromCard')}
                </span>
              </div>
            ) : (
              <select
                className={styles.input}
                value={category}
                onChange={(e) => setCategory(e.target.value as HseCategory)}
              >
                <option value="health">{t('hse.category.health')}</option>
                <option value="safety">{t('hse.category.safety')}</option>
                <option value="environment">
                  {t('hse.category.environment')}
                </option>
              </select>
            )}
          </div>

          {/* Severity — 5-chip picker. Options swap based on category
              (DEV-822 Option B): Health/Safety use OSHA labels, Environment
              uses EPA-aligned labels. */}
          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              {t('hse.modal.field.severity')}
              <span className={styles.req}>*</span>
            </label>
            <div className={styles.severityGrid}>
              {severityOptions.map((sev) => {
                const color = severityColor(sev, category);
                return (
                  <button
                    key={sev}
                    type="button"
                    className={`${styles.sevChip} ${
                      severity === sev ? styles.sevChipSelected : ''
                    }`}
                    data-severity={sev}
                    data-color={color}
                    onClick={() => setSeverity(sev)}
                  >
                    <span className={styles.sevChipLabel}>
                      {t(`hse.severity.${sev}`)}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className={styles.fieldHelp}>
              {category === 'environment'
                ? t(
                    'hse.modal.help.severityColorEnv',
                    'Determines card color: Near-miss & Minor → yellow; Reportable, Major, Catastrophic → red.',
                  )
                : t(
                    'hse.modal.help.severityColor',
                    'Determines card color: Near-miss & First-aid → yellow; Recordable, Lost-time, Fatality → red.',
                  )}
            </div>
          </div>

          {/* Incident date/time */}
          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              {t('hse.modal.field.incidentDate')}
              <span className={styles.req}>*</span>
            </label>
            <input
              type="datetime-local"
              className={styles.input}
              value={incidentDate}
              onChange={(e) => setIncidentDate(e.target.value)}
            />
            <div className={styles.fieldHelp}>
              {t('hse.modal.help.incidentDateImmutable')}
            </div>
          </div>

          {/* Title */}
          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              {t('hse.modal.field.title')}
              <span className={styles.req}>*</span>
            </label>
            <input
              type="text"
              className={styles.input}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('hse.modal.placeholder.title')}
              maxLength={500}
            />
          </div>

          {/* Description */}
          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              {t('hse.modal.field.description')}
              <span className={styles.req}>*</span>
            </label>
            <textarea
              className={styles.input}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('hse.modal.placeholder.description')}
              rows={5}
              maxLength={4000}
            />
            <div className={styles.fieldHelp}>
              {t('hse.modal.help.description')}
            </div>
          </div>

          {/* Attachments (DEV-822 #126) — photos, witness statements, medical
              notes. Uploaded to MinIO via FileStorageController. Picker
              returns the resulting keys; we send them with the incident. */}
          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              {t('hse.modal.field.attachments', 'Attachments')}
            </label>
            <AttachmentPicker
              value={attachmentKeys}
              onChange={setAttachmentKeys}
              category="incidents"
              entityId={ownerGuid}
              accept="image/*,.pdf,.doc,.docx,.txt"
            />
            <div className={styles.fieldHelp}>
              {t(
                'hse.modal.help.attachments',
                'Photos, witness statements, medical notes — anything that documents the incident. Drag files in or click to browse.',
              )}
            </div>
          </div>

          {/* Confidential */}
          <div className={styles.field}>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={effectiveConfidential}
                onChange={(e) => {
                  setConfidential(e.target.checked);
                  setUserTouchedConfidential(true);
                }}
              />
              <span className={styles.checkboxBody}>
                <strong>{t('hse.modal.field.confidential')}</strong>
                <span className={styles.checkboxDesc}>
                  {t('hse.modal.help.confidential')}
                </span>
              </span>
            </label>
          </div>

          {error && (
            <div className={styles.errorBanner} role="alert">
              {error}
            </div>
          )}
        </div>

        <div className={styles.foot}>
          <div className={styles.footNote}>
            {t('hse.modal.foot.createsIssue')} ·{' '}
            <code>HSE Incident</code> · <code>{category}</code>
          </div>
          <div className={styles.footActions}>
            <button
              className={styles.btn}
              type="button"
              onClick={handleClose}
              disabled={submitting}
            >
              {tCommon('cancel')}
            </button>
            <button
              className={`${styles.btn} ${styles.btnPrimary}`}
              type="button"
              onClick={handleSave}
              disabled={submitting}
            >
              {submitting
                ? t('hse.modal.saving')
                : t('hse.modal.save')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default ReportIncidentModal;
